import React, { useMemo, useState } from 'react';
import apis from '@src/apis';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { uploadFileToS3 } from '@src/services/upload';
import { v4 as uuidv4 } from 'uuid';
import { VOICE_NAME_REGEX } from '@src/constants/voiceCloning';
import { StyledShareAndEarn, StyledVoiceDetail } from '../index.style';
import ActionButton from './ActionButtons';
import FormDataInfoVoice from './FormDataInfoVoice';
import AcceptPolicyPublishVoice from './AcceptPolicyPublishVoice';

const MAX_NAME_LENGTH = 25;
const MIN_NAME_LENGTH = 5;
const MAX_DESCRIPTION_LENGTH = 200;

const FormShareAndEarn = ({ voiceCloningInTTS, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { voice } = useSelector((state) => state.voiceVoiceCloning);
  const [step, setStep] = useState(1);
  const [avatar, setAvatar] = useState(voice.avatar);
  const [avatarFile, setAvatarFile] = useState(null);
  const [selectedSampleVoice, setSelectedSampleVoice] = useState(null);
  const [loadingCheckVoiceNameExist, setLoadingCheckVoiceNameExist] =
    useState(false);
  const [voiceInfo, setVoiceInfo] = useState({
    voiceName: voice.name,
    ageGroup: voice.ageGroup,
    description: voice.description || '',
    retentionDays: 365,
    isAcceptPolicy: false,
  });

  const [error, setError] = useState({
    voiceName: false,
    ageGroup: false,
    description: false,
  });

  const disableButtonSubmit = useMemo(
    () => step === 2 && !voiceInfo.isAcceptPolicy,
    [voiceInfo, step],
  );

  const handleCloseDialog = () => {
    if (step === 1) {
      onClose();
    } else {
      setStep(1);
    }
  };

  const handleCheckVoiceNameExist = async () => {
    setLoadingCheckVoiceNameExist(true);
    const response = await apis.voiceCloning.checkVoiceNameExist({
      voiceId: voice.id,
      name: voiceInfo.voiceName,
    });
    setLoadingCheckVoiceNameExist(false);
    let isExist = false;
    if (response.status) {
      isExist = response.result.isExisted;
    }
    return isExist;
  };

  const handleSelect = (e) => {
    setVoiceInfo({ ...voiceInfo, [e.target.name]: e.target.value });
    setError({ ...error, [e.target.name]: false });
  };

  const handleUploadAvatarToS3 = async (fileName, file) => {
    const extension = fileName.split('.')[1];
    const name = fileName.split('.')[0];
    const directory = 'images/voice-cloning';
    const s3Url = await uploadFileToS3(name, directory, extension, file);

    return s3Url;
  };

  const uploadImageToS3 = async (file) => {
    const avatarName = file.name;
    const ext = avatarName.split('.').pop();
    const imgName = avatarName.slice(0, avatarName.lastIndexOf('.'));
    const link = await handleUploadAvatarToS3(
      `${imgName}_${uuidv4()}.${ext}`,
      avatarFile,
    );
    return link;
  };

  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    setAvatar(URL.createObjectURL(file));
    setAvatarFile(file);
  };

  const handlePublishVoice = async () => {
    let avatarUrl = voice.avatar;
    if (avatarFile) {
      avatarUrl = await uploadImageToS3(avatarFile);
    }
    const response = await apis.voiceCloning.publishVoice(voice.id, {
      name: voiceInfo.voiceName.trim(),
      ageGroup: voiceInfo.ageGroup,
      description: voiceInfo.description,
      avatar: avatarUrl,
      retentionDays: voiceInfo.retentionDays,
      agreedToTerm: voiceInfo.isAcceptPolicy,
      ...(selectedSampleVoice && { demoAudioId: selectedSampleVoice?.id }),
    });
    if (response.status) {
      dispatch(
        actions.noti.push({
          message: 'publishVoiceSuccess',
          severity: 'success',
        }),
      );
      dispatch(actions.voiceVoiceCloning.refreshVoicesList(true));
      onClose();
      return;
    }

    dispatch(
      actions.noti.push({
        message: 'publishVoiceFailed',
        severity: 'error',
      }),
    );
  };

  const handleSaveChange = async () => {
    const isExist = await handleCheckVoiceNameExist();
    if (isExist) {
      setError({ ...error, voiceName: t('voiceNameExistsError') });
      return;
    }
    const newError = {};
    const { isAcceptPolicy, ...rest } = voiceInfo;
    Object.keys(rest).forEach((key) => {
      if (key === 'description') {
        return;
      }
      if (!rest[key]) {
        newError[key] = t('requiredField', { field: t(key) });
      }
    });

    if (rest.voiceName) {
      // Normalize the voice name to NFC to avoid issues with Vietnamese characters
      const normalizedVoiceName = rest.voiceName.normalize('NFC');
      if (
        normalizedVoiceName.length < MIN_NAME_LENGTH ||
        normalizedVoiceName.length > MAX_NAME_LENGTH
      )
        newError.voiceName = t('voiceNameDescription');
      const isValidCharacters = VOICE_NAME_REGEX.test(normalizedVoiceName);
      if (!isValidCharacters) newError.voiceName = t('voiceNameDescription');
    }

    if (!rest.description) {
      newError.description = t('requiredField', { field: t('description') });
    }

    if (rest.description) {
      if (rest.description.length > MAX_DESCRIPTION_LENGTH) {
        newError.description = t('errorDescriptionLength', {
          maxLength: MAX_DESCRIPTION_LENGTH,
        });
      }
    }

    if (Object.values(newError).some((value) => value)) {
      setError(newError);
      return;
    }
    if (step === 1) {
      setStep(2);
    } else {
      handlePublishVoice();
    }
  };

  const handleSelectRetentionDays = (value) => {
    setVoiceInfo({ ...voiceInfo, retentionDays: value });
  };

  const handleSetIsAcceptPolicy = (value) => {
    setVoiceInfo({ ...voiceInfo, isAcceptPolicy: value });
  };

  const labelButtonNext = useMemo(() => {
    if (step === 1) {
      return t('next');
    }
    return t('sendRequest');
  }, [step]);

  return (
    <StyledShareAndEarn>
      <StyledVoiceDetail>
        {step === 1 && (
          <FormDataInfoVoice
            handleCloseDialog={handleCloseDialog}
            avatar={avatar}
            voiceInfo={voiceInfo}
            handleSelect={handleSelect}
            error={error}
            handleAvatarChange={handleAvatarChange}
            voiceCloningInTTS={voiceCloningInTTS}
            voice={voice}
            selectedSampleVoice={selectedSampleVoice}
            setSelectedSampleVoice={setSelectedSampleVoice}
          />
        )}
        {step === 2 && (
          <AcceptPolicyPublishVoice
            handleSelectRetentionDays={handleSelectRetentionDays}
            retentionDays={voiceInfo.retentionDays}
            isLoadingPublishVoice={false}
            handleCloseDialog={handleCloseDialog}
            isAcceptPolicy={voiceInfo.isAcceptPolicy}
            setIsAcceptPolicy={handleSetIsAcceptPolicy}
          />
        )}
        <ActionButton
          labelGoBack={t('goBack')}
          labelNext={labelButtonNext}
          onGoBack={handleCloseDialog}
          onNext={handleSaveChange}
          disabledNext={loadingCheckVoiceNameExist || disableButtonSubmit}
        />
      </StyledVoiceDetail>
    </StyledShareAndEarn>
  );
};

export default FormShareAndEarn;
